import { Component, inject, signal, computed, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { brandColor, brandAlpha } from '../../services/theme';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import {
  AdminDashboardService,
  AdminDashboardFilter,
  AdminFilterOptions,
  AdminPaymentRow,
  AdminMemberRow,
  AdminEntityRow,
} from '../../services/admin-dashboard';
import { ToastService } from '../../services/toast';
import { IndDatePipe } from '../../pipes/ind-date';
import { CoiView } from '../policies/coi-view/coi-view';
import { ReceiptView } from '../payments/receipt/receipt-view';

type TabKey = 'payments' | 'members' | 'coi' | 'partnerwise' | 'clientwise';
type PeriodMode = 'fy' | 'cal' | 'custom';

/** Validated chart palette (dataviz-checked against a white card surface).
 *  The two brand entries are GETTERS: a plain value would be captured once at module load
 *  and freeze the charts on whatever accent was active then. The rest are semantic
 *  (pending / client / …) and deliberately fixed. */
const C = {
  get brand() { return brandColor('--brand'); },
  get brandDark() { return brandColor('--brand-dark'); },
  pending: '#eda100',
  client: '#4a3aa7',
  grid: '#eef2f6',
  tick: '#64748b',
  status: {
    SUCCESS: '#16a34a',
    PAID: '#16a34a',
    PENDING: '#d97706',
    UNPAID: '#d97706',
    INITIATED: brandColor('--brand'),
    FAILED: '#dc2626',
    CANCELLED: '#9333ea',
    REFUNDED: '#0d9488',
  } as Record<string, string>,
};

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChartModule,
    TableModule,
    TagModule,
    ButtonModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    TabsModule,
    TooltipModule,
    DialogModule,
    SkeletonModule,
    IconFieldModule,
    InputIconModule,
    IndDatePipe,
    CoiView,
    ReceiptView,
  ],
  templateUrl: './admin-dashboard.html',
  styleUrls: ['./admin-dashboard.css'],
})
export class AdminDashboardPage implements OnInit, OnDestroy {
  private svc = inject(AdminDashboardService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  // ===================== filter bar (draft state; Apply copies into `applied`) =====================
  options = signal<AdminFilterOptions | null>(null);
  fPartner = signal<number | null>(null);
  fClient = signal<number | null>(null);
  fSource = signal<string | null>(null);
  fPolicy = signal<number | null>(null);
  fGateway = signal<number | null>(null);
  fCoi = signal<string | null>(null);
  fKyc = signal<string | null>(null);
  periodMode = signal<PeriodMode>('fy');
  fFinYear = signal<number | null>(null);
  fYear = signal<number | null>(null);
  fMonth = signal<number | null>(null);
  fRange = signal<Date[] | null>(null);
  fSearch = signal('');

  /** The filter every load actually uses — set by Apply/Reset/drill-in. */
  private applied = signal<AdminDashboardFilter>({});

  // Typed so the segmented buttons can pass `m.value` straight into periodMode.set() —
  // without the annotation it widens to `string` and no longer matches PeriodMode.
  readonly periodModes: { label: string; value: PeriodMode }[] = [
    { label: 'Financial Year', value: 'fy' },
    { label: 'Calendar', value: 'cal' },
    { label: 'Custom range', value: 'custom' },
  ];

  /** Secondary filters start folded: seven equal dropdowns read as a wall, and four of them
   *  are used rarely. The chip row below still shows anything active while folded. */
  readonly showMoreFilters = signal(false);

  /** Chips for the filters actually in force, so a folded panel never hides a live filter. */
  readonly activeChips = computed(() => {
    const o = this.options();
    const name = (list: { id: number; name: string }[] | undefined, id: number | null) =>
      list?.find((x) => x.id === id)?.name ?? String(id ?? '');
    const chips: { key: string; label: string; value: string }[] = [];
    if (this.fPartner() != null) chips.push({ key: 'partner', label: 'Partner', value: name(o?.partners, this.fPartner()) });
    if (this.fClient() != null) chips.push({ key: 'client', label: 'Client', value: name(o?.clients, this.fClient()) });
    if (this.fSource()) chips.push({ key: 'source', label: 'Source', value: this.fSource()! });
    if (this.fPolicy() != null) chips.push({ key: 'policy', label: 'Policy', value: name(o?.masterPolicies, this.fPolicy()) });
    if (this.fGateway() != null) chips.push({ key: 'gateway', label: 'Gateway', value: name(o?.gateways, this.fGateway()) });
    if (this.fCoi()) chips.push({ key: 'coi', label: 'COI', value: this.fCoi()! });
    if (this.fKyc()) chips.push({ key: 'kyc', label: 'KYC', value: this.fKyc()! });
    if (this.fSearch().trim()) chips.push({ key: 'search', label: 'Search', value: this.fSearch().trim() });
    return chips;
  });

  /** Drop one chip and re-run immediately — a chip you must then "Apply" isn't a chip. */
  clearChip(key: string) {
    switch (key) {
      case 'partner': this.fPartner.set(null); break;
      case 'client': this.fClient.set(null); break;
      case 'source': this.fSource.set(null); break;
      case 'policy': this.fPolicy.set(null); break;
      case 'gateway': this.fGateway.set(null); break;
      case 'coi': this.fCoi.set(null); break;
      case 'kyc': this.fKyc.set(null); break;
      case 'search': this.fSearch.set(''); break;
    }
    this.apply();
  }
  fyOptions: { label: string; value: number }[] = [];
  yearOptions: { label: string; value: number }[] = [];
  readonly monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ].map((m, i) => ({ label: m, value: i + 1 }));

  // ===================== overview =====================
  loadingOverview = signal(false);
  overview = signal<any | null>(null);
  readonly summary = computed(() => this.overview()?.summary ?? null);
  readonly entityCounts = computed(() => this.overview()?.entityCounts ?? null);
  readonly topPartners = computed<AdminEntityRow[]>(() => this.overview()?.topPartners ?? []);
  readonly topClients = computed<AdminEntityRow[]>(() => this.overview()?.topClients ?? []);

  /** Transaction roll-up. The API has always returned this; nothing rendered it until now. */
  readonly paymentStats = computed(() => this.overview()?.paymentStats ?? null);

  /** Latest transactions — also already returned and previously discarded. */
  readonly recentPayments = computed<AdminPaymentRow[]>(() => this.overview()?.recentPayments ?? []);

  /** When the overview last came back, so the header can say how fresh the figures are. */
  readonly lastLoaded = signal<Date | null>(null);

  // ---------- derived KPIs (no backend change: all of these fall out of data we already have) ----------
  private static pct(part: number, whole: number): number {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
  }

  /** Share of billed premium actually banked. */
  readonly collectionRate = computed(() => {
    const s = this.summary();
    return AdminDashboardPage.pct(Number(s?.collectedAmount ?? 0), Number(s?.totalPremium ?? 0));
  });

  /** Gateway health: successful attempts as a share of all attempts. */
  readonly successRate = computed(() => {
    const p = this.paymentStats();
    return AdminDashboardPage.pct(Number(p?.successCount ?? 0), Number(p?.totalTransactions ?? 0));
  });

  readonly issuedRate = computed(() => {
    const s = this.summary();
    return AdminDashboardPage.pct(Number(s?.policiesIssued ?? 0), Number(s?.paidMembers ?? 0));
  });

  readonly kycRate = computed(() => {
    const s = this.summary();
    return AdminDashboardPage.pct(Number(s?.kycVerified ?? 0), Number(s?.totalMembers ?? 0));
  });

  /** Average premium per enrolled member — whole rupees, like every other amount. */
  readonly avgPremium = computed(() => {
    const s = this.summary();
    const n = Number(s?.totalMembers ?? 0);
    return n > 0 ? Math.round(Number(s?.totalPremium ?? 0) / n) : 0;
  });

  /**
   * Partner / client split as plain percentages. Rendered as a bar rather than a doughnut:
   * a two-slice doughnut needs 300px of card to say what a 12px bar says, which is most of
   * why the old layout looked empty.
   */
  readonly sourceSplit = computed(() => {
    const s = this.summary();
    const partner = Number(s?.partnerMembers ?? 0);
    const client = Number(s?.clientMembers ?? 0);
    const total = partner + client;
    return {
      partner,
      client,
      total,
      partnerPct: AdminDashboardPage.pct(partner, total),
      clientPct: AdminDashboardPage.pct(client, total),
    };
  });

  // ---------- empty-state flags ----------
  // A Chart.js canvas with nothing to plot renders as a blank rectangle, which is exactly what
  // made three tall cards read as dead space. Ask whether there is anything worth drawing and
  // show a real empty state when there isn't.
  readonly hasTrend = computed(() =>
    ((this.overview()?.monthlyTrend ?? []) as any[]).some(
      (t) => Number(t.members ?? 0) > 0 || Number(t.collected ?? 0) > 0 || Number(t.pending ?? 0) > 0,
    ),
  );

  readonly hasStatusData = computed(() =>
    ((this.overview()?.paymentStatusBreakdown ?? []) as any[]).some((r) => Number(r.count ?? 0) > 0),
  );

  /** A line needs two points to be a line. With a single month it was 100px of empty grid
   *  next to one dot, so the enrolment series only renders once there is a trend to show. */
  readonly multiMonth = computed(() => ((this.overview()?.monthlyTrend ?? []) as any[]).length >= 2);

  // Charts (built after Overview loads; p-chart renders browser-only).
  trendBars: any = null;
  trendLine: any = null;
  statusDoughnut: any = null;
  issuedBars: any = null;
  doughnutMode = signal<'count' | 'amount'>('count');

  readonly barOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: '#475569', usePointStyle: true } } },
    scales: {
      x: { ticks: { color: C.tick }, grid: { display: false } },
      y: { ticks: { color: C.tick }, grid: { color: C.grid } },
    },
  };
  readonly lineOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: C.tick }, grid: { display: false } },
      y: { ticks: { color: C.tick, precision: 0 }, grid: { color: C.grid } },
    },
  };
  readonly doughnutOptions = {
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: { legend: { position: 'bottom', labels: { color: '#475569', usePointStyle: true } } },
  };

  // ===================== tabs =====================
  activeTab = signal<TabKey>('payments');
  /** Tabs already loaded for the CURRENT applied filter (cleared on Apply). */
  private loadedTabs = new Set<TabKey>();

  // Payments tab
  payRows = signal<AdminPaymentRow[]>([]);
  payTotal = signal(0);
  payLoading = signal(false);
  payPage = signal(1);
  payPageSize = signal(10);
  payStatus = signal<string | null>(null);
  payGateway = signal<number | null>(null);
  paySearch = signal('');

  // Members tab
  memRows = signal<AdminMemberRow[]>([]);
  memTotal = signal(0);
  memLoading = signal(false);
  memPage = signal(1);
  memPageSize = signal(10);
  memStatus = signal<string | null>(null);
  memKyc = signal<string | null>(null);
  memCoi = signal<string | null>(null);
  memSearch = signal('');

  // COI tab
  coiLoading = signal(false);
  coiData = signal<any | null>(null);

  // Partner-wise / Client-wise tabs
  pwRows = signal<AdminEntityRow[]>([]);
  pwLoading = signal(false);
  pwSearch = signal('');
  cwRows = signal<AdminEntityRow[]>([]);
  cwLoading = signal(false);
  cwSearch = signal('');

  private searchTimer: any;

  // Dialogs (COI viewer / receipt) — same pattern as payments-list.
  showCoi = signal(false);
  coiMemberId = signal<number | null>(null);
  showReceipt = signal(false);
  receiptPaymentId = signal<number | null>(null);

  // ===================== lifecycle =====================
  ngOnInit() {
    if (!this.isBrowser) return;

    const now = new Date();
    const fy = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    this.fyOptions = Array.from({ length: 5 }, (_, i) => {
      const y = fy - i;
      return { label: `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`, value: y };
    });
    this.yearOptions = Array.from({ length: 5 }, (_, i) => ({
      label: String(now.getFullYear() - i),
      value: now.getFullYear() - i,
    }));
    this.fFinYear.set(fy);
    this.applied.set({ finYear: fy });

    this.svc.filterOptions().subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load filter options.');
          return;
        }
        this.options.set(res?.data ?? null);
      },
      error: (err) => this.handleHttpError(err, 'Could not load filter options.'),
    });

    this.loadOverview();
    this.loadActiveTab(true);
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  /** Non-admins get 403 from every endpoint — bounce them out once, politely. */
  private handleHttpError(err: any, fallback: string) {
    if (err?.status === 403) {
      this.toast.error('The admin dashboard is available to admins only.');
      this.router.navigate(['/dashboard']);
      return;
    }
    this.toast.error(err?.error?.message || fallback);
  }

  // ===================== filter bar =====================
  /** Picking a client (or source CLIENT) makes the partner pick irrelevant — and vice-versa. */
  onPartnerPick(v: number | null) {
    this.fPartner.set(v);
    if (v != null) {
      this.fClient.set(null);
      if (this.fSource() === 'CLIENT') this.fSource.set(null);
    }
  }

  onClientPick(v: number | null) {
    this.fClient.set(v);
    if (v != null) {
      this.fPartner.set(null);
      if (this.fSource() === 'PARTNER') this.fSource.set(null);
    }
  }

  onSourcePick(v: string | null) {
    this.fSource.set(v);
    if (v === 'CLIENT') this.fPartner.set(null);
    if (v === 'PARTNER') this.fClient.set(null);
  }

  private buildFilter(): AdminDashboardFilter {
    const f: AdminDashboardFilter = {};
    if (this.fPartner() != null) f.partnerId = this.fPartner()!;
    if (this.fClient() != null) f.clientId = this.fClient()!;
    if (this.fSource()) f.source = this.fSource()!;
    if (this.fPolicy() != null) f.masterPolicyId = this.fPolicy()!;
    if (this.fGateway() != null) f.gatewayId = this.fGateway()!;
    if (this.fCoi()) f.coiStatus = this.fCoi()!;
    if (this.fKyc()) f.kycStatus = this.fKyc()!;
    const s = this.fSearch().trim();
    if (s) f.search = s;

    // Only the chosen period mode's params are sent.
    const mode = this.periodMode();
    if (mode === 'fy' && this.fFinYear() != null) {
      f.finYear = this.fFinYear()!;
    } else if (mode === 'cal' && this.fYear() != null) {
      f.year = this.fYear()!;
      if (this.fMonth() != null) f.month = this.fMonth()!;
    } else if (mode === 'custom') {
      const [from, to] = this.fRange() ?? [];
      if (from) f.fromDate = this.toIso(from);
      if (to) f.toDate = this.toIso(to);
    }
    return f;
  }

  apply() {
    this.applied.set(this.buildFilter());
    this.loadedTabs.clear();
    this.loadOverview();
    this.loadActiveTab(true);
  }

  reset() {
    this.fPartner.set(null);
    this.fClient.set(null);
    this.fSource.set(null);
    this.fPolicy.set(null);
    this.fGateway.set(null);
    this.fCoi.set(null);
    this.fKyc.set(null);
    this.fSearch.set('');
    this.periodMode.set('fy');
    this.fFinYear.set(this.fyOptions[0]?.value ?? null);
    this.fYear.set(null);
    this.fMonth.set(null);
    this.fRange.set(null);
    this.apply();
  }

  // ===================== overview + charts =====================
  loadOverview() {
    this.loadingOverview.set(true);
    this.svc.overview(this.applied()).subscribe({
      next: (res: any) => {
        this.loadingOverview.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the overview.');
          return;
        }
        this.overview.set(res?.data ?? null);
        this.lastLoaded.set(new Date());
        this.buildCharts(res?.data);
      },
      error: (err) => {
        this.loadingOverview.set(false);
        this.handleHttpError(err, 'Could not load the overview.');
      },
    });
  }

  private buildCharts(d: any) {
    if (!d) return;
    const trend: any[] = d.monthlyTrend ?? [];
    const labels = trend.map((t) => t.label);

    // ₹ bars and the members line are SEPARATE charts on one x-axis — money and
    // head-counts are different scales, and dual-axis charts misread (dataviz rule).
    this.trendBars = {
      labels,
      datasets: [
        { label: 'Collected ₹', data: trend.map((t) => t.collected), backgroundColor: C.brand, borderRadius: 4, maxBarThickness: 26 },
        { label: 'Pending ₹', data: trend.map((t) => t.pending), backgroundColor: C.pending, borderRadius: 4, maxBarThickness: 26 },
      ],
    };
    this.trendLine = {
      labels,
      datasets: [
        {
          label: 'Members',
          data: trend.map((t) => t.members),
          borderColor: C.brand,
          backgroundColor: brandAlpha(0.1),
          pointBackgroundColor: C.brand,
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
      ],
    };

    // The partner/client split is rendered as a bar now (see sourceSplit()), not a doughnut.
    this.rebuildStatusDoughnut();
  }

  rebuildStatusDoughnut() {
    const rows: any[] = this.overview()?.paymentStatusBreakdown ?? [];
    const byAmount = this.doughnutMode() === 'amount';
    this.statusDoughnut = {
      labels: rows.map((r) => r.paymentStatus),
      datasets: [
        {
          data: rows.map((r) => (byAmount ? r.amount : r.count)),
          backgroundColor: rows.map((r) => C.status[(r.paymentStatus || '').toUpperCase()] ?? '#64748b'),
        },
      ],
    };
  }

  toggleDoughnutMode() {
    this.doughnutMode.set(this.doughnutMode() === 'count' ? 'amount' : 'count');
    this.rebuildStatusDoughnut();
  }

  // ===================== tabs =====================
  onTabChange(v: any) {
    this.activeTab.set(v as TabKey);
    this.loadActiveTab(false);
  }

  /** Load the active tab; `force` reloads even if it already loaded for this filter. */
  private loadActiveTab(force: boolean) {
    const tab = this.activeTab();
    if (!force && this.loadedTabs.has(tab)) return;
    this.loadedTabs.add(tab);
    switch (tab) {
      case 'payments': this.payPage.set(1); this.loadPayments(); break;
      case 'members': this.memPage.set(1); this.loadMembers(); break;
      case 'coi': this.loadCoi(); break;
      case 'partnerwise': this.loadPartnerWise(); break;
      case 'clientwise': this.loadClientWise(); break;
    }
  }

  /** Debounce a per-table search box, then run `reload`. */
  onTableSearch(sig: { set(v: string): void }, value: string, reload: () => void) {
    sig.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(reload, 350);
  }

  // ---- payments ----
  loadPayments() {
    this.payLoading.set(true);
    const f: AdminDashboardFilter = {
      ...this.applied(),
      page: this.payPage(),
      pageSize: this.payPageSize(),
    };
    if (this.payStatus()) f.paymentStatus = this.payStatus()!;
    if (this.payGateway() != null) f.gatewayId = this.payGateway()!;
    const s = this.paySearch().trim();
    if (s) f.search = s;
    this.svc.payments(f).subscribe({
      next: (res: any) => {
        this.payLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load payments.');
          return;
        }
        this.payRows.set(res?.data?.items ?? []);
        this.payTotal.set(res?.data?.totalCount ?? 0);
      },
      error: (err) => {
        this.payLoading.set(false);
        this.handleHttpError(err, 'Could not load payments.');
      },
    });
  }

  onPayLazy(e: any) {
    const size = e?.rows ?? this.payPageSize();
    this.payPage.set(Math.floor((e?.first ?? 0) / size) + 1);
    this.payPageSize.set(size);
    this.loadPayments();
  }

  // ---- members ----
  loadMembers() {
    this.memLoading.set(true);
    const f: AdminDashboardFilter = {
      ...this.applied(),
      page: this.memPage(),
      pageSize: this.memPageSize(),
    };
    if (this.memStatus()) f.paymentStatus = this.memStatus()!;
    if (this.memKyc()) f.kycStatus = this.memKyc()!;
    if (this.memCoi()) f.coiStatus = this.memCoi()!;
    const s = this.memSearch().trim();
    if (s) f.search = s;
    this.svc.members(f).subscribe({
      next: (res: any) => {
        this.memLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load members.');
          return;
        }
        this.memRows.set(res?.data?.items ?? []);
        this.memTotal.set(res?.data?.totalCount ?? 0);
      },
      error: (err) => {
        this.memLoading.set(false);
        this.handleHttpError(err, 'Could not load members.');
      },
    });
  }

  onMemLazy(e: any) {
    const size = e?.rows ?? this.memPageSize();
    this.memPage.set(Math.floor((e?.first ?? 0) / size) + 1);
    this.memPageSize.set(size);
    this.loadMembers();
  }

  // ---- COI ----
  loadCoi() {
    this.coiLoading.set(true);
    this.svc.coiSummary(this.applied()).subscribe({
      next: (res: any) => {
        this.coiLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the COI summary.');
          return;
        }
        this.coiData.set(res?.data ?? null);
        const trend: any[] = res?.data?.monthlyIssuedTrend ?? [];
        this.issuedBars = {
          labels: trend.map((t) => t.label),
          datasets: [
            { label: 'Certificates issued', data: trend.map((t) => t.issued), backgroundColor: C.brand, borderRadius: 4, maxBarThickness: 26 },
          ],
        };
      },
      error: (err) => {
        this.coiLoading.set(false);
        this.handleHttpError(err, 'Could not load the COI summary.');
      },
    });
  }

  /** COI tab → "view all issued": jump to Members with the COI filter pre-set. */
  viewAllIssued() {
    this.memCoi.set('GENERATED');
    this.activeTab.set('members');
    this.memPage.set(1);
    this.loadMembers();
    this.loadedTabs.add('members');
  }

  // ---- partner-wise / client-wise ----
  loadPartnerWise() {
    this.pwLoading.set(true);
    const f = { ...this.applied() };
    const s = this.pwSearch().trim();
    if (s) f.search = s;
    this.svc.partnerWise(f).subscribe({
      next: (res: any) => {
        this.pwLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the partner summary.');
          return;
        }
        this.pwRows.set(res?.data?.rows ?? []);
      },
      error: (err) => {
        this.pwLoading.set(false);
        this.handleHttpError(err, 'Could not load the partner summary.');
      },
    });
  }

  loadClientWise() {
    this.cwLoading.set(true);
    const f = { ...this.applied() };
    const s = this.cwSearch().trim();
    if (s) f.search = s;
    this.svc.clientWise(f).subscribe({
      next: (res: any) => {
        this.cwLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load the client summary.');
          return;
        }
        this.cwRows.set(res?.data?.rows ?? []);
      },
      error: (err) => {
        this.cwLoading.set(false);
        this.handleHttpError(err, 'Could not load the client summary.');
      },
    });
  }

  /** Row click drill-in: focus one partner/client and jump to its payments. */
  drillPartner(row: AdminEntityRow) {
    if (!row.partnerId) return;
    this.onPartnerPick(row.partnerId);
    this.apply();
    this.activeTab.set('payments');
  }

  drillClient(row: AdminEntityRow) {
    if (!row.clientId) return;
    this.onClientPick(row.clientId);
    this.apply();
    this.activeTab.set('payments');
  }

  // ===================== dialogs =====================
  viewCoi(memberId?: number | null) {
    if (!memberId) return;
    this.coiMemberId.set(memberId);
    this.showCoi.set(true);
  }

  viewReceipt(paymentId?: number | null) {
    if (!paymentId) return;
    this.receiptPaymentId.set(paymentId);
    this.showReceipt.set(true);
  }

  // ===================== display helpers =====================
  inr(v?: number | null): string {
    return '₹' + Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  /** Compact money for tiles: ₹12.4L / ₹3.2Cr, so a big figure never wraps the card. */
  inrShort(v?: number | null): string {
    const n = Math.round(Number(v ?? 0));
    const a = Math.abs(n);
    if (a >= 1e7) return '₹' + (n / 1e7).toFixed(a >= 1e8 ? 0 : 1).replace(/\.0$/, '') + 'Cr';
    if (a >= 1e5) return '₹' + (n / 1e5).toFixed(a >= 1e6 ? 0 : 1).replace(/\.0$/, '') + 'L';
    return '₹' + n.toLocaleString('en-IN');
  }

  num(v?: number | null): string {
    return Number(v ?? 0).toLocaleString('en-IN');
  }

  /** Refresh just the figures, keeping the filter and the open tab as they are. */
  refresh() {
    this.loadOverview();
    this.loadedTabs.delete(this.activeTab());
    this.loadActiveTab(true);
  }

  paySeverity(s?: string | null): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch ((s || '').toUpperCase()) {
      case 'SUCCESS':
      case 'PAID':
        return 'success';
      case 'PENDING':
      case 'UNPAID':
        return 'warn';
      case 'INITIATED':
        return 'info';
      case 'FAILED':
      case 'CANCELLED':
        return 'danger';
      case 'REFUNDED':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  kycSeverity(s?: string | null): 'success' | 'danger' | 'warn' | 'secondary' {
    switch ((s || '').toUpperCase()) {
      case 'VERIFIED':
        return 'success';
      case 'PENDING':
        return 'warn';
      case 'NOT_VERIFIED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  srcSeverity(s?: string | null): 'info' | 'secondary' {
    return (s || '').toUpperCase() === 'PARTNER' ? 'info' : 'secondary';
  }

  ownerOf(r: { partnerName?: string | null; clientName?: string | null }): string {
    return r.partnerName || r.clientName || '—';
  }

  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
